import { sinVerComo } from "@/lib/impersonation/ver-como-lectura";
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { usePublishImpersonationTarget } from "@/contexts/ImpersonationTargetContext";

/**
 * "Ver como" del Portal Inmobiliaria.
 *
 * Quien puede impersonar elige una inmobiliaria (un usuario con rol Inmobiliaria) y el portal se
 * ve EXACTAMENTE como lo ve ese usuario:
 *   - Lecturas: las RPC del portal reciben `p_auth_user_id` (el `auth_user_id` del impersonado) y
 *     el servidor calcula permiso, inmobiliaria y datos con esa identidad. Lo valida contra
 *     `puede_impersonar` del usuario real y contra el menú de su rol (nunca abre un menú que el
 *     rol real no tiene).
 *   - Escrituras: ninguna mientras se impersona (`useInmobPermisos` apaga crear/editar/eliminar)
 *     y el servidor siempre firma con la sesión real.
 * El rol y el `auth_user_id` del impersonado se leen de `usuarios` (activo); si no existe, está
 * inactivo, no tiene cuenta auth o el usuario real no tiene `puede_impersonar`, se descarta.
 */

interface InmobiliariaImpersonationContextType {
  impersonatedInmobiliariaEmail: string | null;
  impersonatedInmobiliariaPersonaId: number | null;
  impersonatedInmobiliariaName: string | null;
  /** `usuarios.auth_user_id` del impersonado: lo reciben las RPC de lectura ("ver como"). */
  impersonatedAuthUserId: string | null;
  /** Rol con el que entra al portal (para pintar su menú): el de "Ver como" o su rol principal. */
  impersonatedRolId: number | null;
  impersonatedRolNombre: string | null;
  setImpersonatedInmobiliaria: (
    email: string | null, personaId: number | null, name: string | null,
    rolId?: number | null, rolNombre?: string | null,
  ) => void;
  clearImpersonation: () => void;
  /** Hay un impersonado VÁLIDO (resuelto en BD). */
  isImpersonating: boolean;
  /** Se eligió a alguien y todavía se valida: las pantallas esperan y no escriben. */
  impersonacionPendiente: boolean;
}

const InmobiliariaImpersonationContext = createContext<InmobiliariaImpersonationContextType>({
  impersonatedInmobiliariaEmail: null,
  impersonatedInmobiliariaPersonaId: null,
  impersonatedInmobiliariaName: null,
  impersonatedAuthUserId: null,
  impersonatedRolId: null,
  impersonatedRolNombre: null,
  setImpersonatedInmobiliaria: () => {},
  clearImpersonation: () => {},
  isImpersonating: false,
  impersonacionPendiente: false,
});

export function InmobiliariaImpersonationProvider({ children }: { children: ReactNode }) {
  const { profile, isProfileLoading } = useAuth();
  const [email, setEmail] = useState<string | null>(null);
  const [personaId, setPersonaId] = useState<number | null>(null);
  const [name, setName] = useState<string | null>(null);
  // Rol con el que entra al portal según "Ver como" (puede ser un rol adicional).
  const [rolAcceso, setRolAcceso] = useState<{ id: number | null; nombre: string | null }>({ id: null, nombre: null });

  const puedeImpersonar = profile?.puede_impersonar === true;
  const sinDerecho = !!profile && !isProfileLoading && !puedeImpersonar;

  const objetivo = useQuery({
    queryKey: ["inmob-impersonado", email],
    enabled: puedeImpersonar && !!email,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      // Con la sesión real: con "ver como" activo, la lectura de otra persona saldría con el
      // acceso de la anterior y el cambio de persona fallaría.
      const { data, error } = await sinVerComo((supabase as any)
        .from("usuarios")
        .select("rol_id, id_persona, auth_user_id")
        .eq("email", email)
        .eq("activo", true))
        .maybeSingle();
      if (error) throw error;
      return (data as { rol_id: number | null; id_persona: number | null; auth_user_id: string | null } | null) ?? null;
    },
  });
  // Sin fila activa, sin rol o sin cuenta auth: el servidor no podría calcular su vista.
  const objetivoInvalido =
    objetivo.isError ||
    (objetivo.isSuccess && (objetivo.data == null || objetivo.data.rol_id == null || !objetivo.data.auth_user_id));

  const clearImpersonation = () => {
    setEmail(null);
    setPersonaId(null);
    setName(null);
    setRolAcceso({ id: null, nombre: null });
  };

  useEffect(() => {
    if (email && (sinDerecho || objetivoInvalido)) clearImpersonation();
  }, [email, sinDerecho, objetivoInvalido]);

  const setImpersonatedInmobiliaria = (
    e: string | null, p: number | null, n: string | null, rid?: number | null, rn?: string | null,
  ) => {
    if (!puedeImpersonar) return;
    setEmail(e);
    setPersonaId(p);
    setName(n);
    setRolAcceso({ id: rid ?? null, nombre: rn ?? null });
  };

  const activo = !!email && puedeImpersonar && objetivo.isSuccess && !objetivoInvalido;
  const pendiente = !!email && !sinDerecho && !objetivoInvalido && !objetivo.isSuccess;

  return (
    <InmobiliariaImpersonationContext.Provider
      value={{
        impersonatedInmobiliariaEmail: activo || pendiente ? email : null,
        impersonatedInmobiliariaPersonaId: activo ? objetivo.data?.id_persona ?? personaId : null,
        impersonatedInmobiliariaName: activo || pendiente ? name : null,
        impersonatedAuthUserId: activo ? objetivo.data?.auth_user_id ?? null : null,
        impersonatedRolId: activo ? rolAcceso.id ?? objetivo.data?.rol_id ?? null : null,
        impersonatedRolNombre: activo ? rolAcceso.nombre : null,
        setImpersonatedInmobiliaria,
        clearImpersonation,
        isImpersonating: activo,
        impersonacionPendiente: pendiente,
      }}
    >
      {children}
    </InmobiliariaImpersonationContext.Provider>
  );
}

export function useInmobiliariaImpersonation() {
  return useContext(InmobiliariaImpersonationContext);
}

/**
 * Publica el impersonado de este contexto al núcleo global (menús/rol efectivos y
 * solo lectura de toda la app). Se llama SOLO desde el layout del portal: el
 * provider vive montado en toda la app y conserva la selección, pero al salir del
 * portal el layout se desmonta, se despublica y la app deja de estar en solo
 * lectura; al volver se republica.
 */
export function usePublicarVerComoInmobiliaria(): void {
  const {
    impersonatedInmobiliariaEmail: email,
    impersonatedInmobiliariaPersonaId: personaId,
    impersonatedInmobiliariaName: nombre,
    impersonatedRolId: rolId,
    impersonatedRolNombre: rolNombre,
    impersonatedAuthUserId: authUserId,
  } = useInmobiliariaImpersonation();
  // authUserId: las lecturas del servidor responden como la persona (header `x-ver-como`).
  usePublishImpersonationTarget(email ? { email, personaId, nombre, rolId, rolNombre, authUserId } : null);
}
